import type { CSSInterpolation } from '@emotion/serialize';
import type { ControllerRenderProps } from 'react-hook-form';

import { useField } from '@adflow/frontend/util-common';

import { FormControl } from '../FormControl';

import type { HubAutocompleteProps } from './HubAutocomplete';
import { HubAutocomplete } from './HubAutocomplete';

export interface HubAutocompleteWithControlProps<
	T,
	DisableClearable extends boolean | undefined = false,
	FreeSolo extends boolean | undefined = false,
> extends Omit<
	HubAutocompleteProps<T, DisableClearable, FreeSolo>,
	'value' | 'onChange'
> {
	label?: string;
	name: string;
	onChangeHandler?: (
		value: T,
		onChange: ControllerRenderProps['onChange']
	) => void;
	displayValue?: (value: ControllerRenderProps['value']) => T;
	outerStyle?: CSSInterpolation;
	helperStyle?: CSSInterpolation;
}

export function HubAutocompleteWithControl<
	T,
	DisableClearable extends boolean | undefined = false,
	FreeSolo extends boolean | undefined = false,
>({
	name,
	label,
	outerStyle,
	helperStyle,
	onChangeHandler,
	displayValue,
	...other
}: HubAutocompleteWithControlProps<T, DisableClearable, FreeSolo>) {
	const {
		field: { onChange, value, ref },
		fieldState: { invalid: error },
		errorMessage,
	} = useField<ControllerRenderProps['value']>({ name, label });

	return (
		<FormControl
			error={error}
			helperText={errorMessage}
			label={label}
			outerStyle={outerStyle}
			helperStyle={helperStyle}
		>
			<HubAutocomplete
				{...other}
				inputRef={ref}
				value={displayValue ? displayValue(value) : (value ?? '')}
				onChange={(args) =>
					onChangeHandler ? onChangeHandler(args, onChange) : onChange(args)
				}
				error={!!errorMessage}
			/>
		</FormControl>
	);
}
